import type React from 'react'
import type { ReactNode } from 'react'

import { styled } from 'styled-components'
import type { KnownTarget } from 'styled-components/dist/types'

import { Button, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import FilterCheckbox from './FilterCheckbox'

// Vertical padding for a single checkbox row. Exported so sibling rows appended
// to the same list (e.g. the Accepts Credit Card toggle in BuyingFormatFilter)
// stay flush with the categorical options instead of re-typing the literal.
export const checkboxRowPadding = '0.125rem 0'

type OnlyableCheckboxProps = {
  as?: KnownTarget
  displayName: ReactNode
  checked: boolean | 'PARTIAL'
  onChange: () => void
  onOnly: () => void
  disabled?: boolean
  onFilterClick?: () => void
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
}

const StyledOnlyButton = styled(Button)`
  margin-left: 4px;
  && {
    height: 22px;
    min-width: 40px;
  }
  position: absolute;
  right: 1rem;
  background: ${designColors.primary.hoverOverlay};

  @media ${Breakpoints.min.medium} {
    right: 0;
  }
`

const Container = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  padding: ${checkboxRowPadding};

  /* Fix mobile touch issues */
  touch-action: manipulation;

  /* Disable hover effects on touch devices to prevent iOS double-tap */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${designColors.primary.hoverOverlay};
      color: ${designColors.primary.default};
    }

    &:hover ${StyledOnlyButton} {
      visibility: visible;
    }
  }

  ${StyledOnlyButton} {
    visibility: hidden;
  }

  label {
    align-items: center;
  }

  /* Show only button on mobile only when checkbox is checked */
  @media (hover: none) and (pointer: coarse) {
    &[data-checked='true'] ${StyledOnlyButton} {
      visibility: visible;
    }
  }
`

const OnlyableCheckbox = ({
  as,
  displayName,
  checked,
  onChange,
  onOnly,
  disabled,
  onFilterClick,
  onClick,
}: OnlyableCheckboxProps) => {
  const handleOnOnlyClick = () => {
    onOnly()
    if (onFilterClick !== undefined) {
      onFilterClick()
    }
  }

  return (
    <Container
      as={as}
      data-checked={checked === true || checked === 'PARTIAL'}
      onMouseDown={(e: React.MouseEvent<HTMLElement>) => {
        if ((e.target as HTMLElement).closest('button')) return
        onFilterClick?.()
        onClick?.(e)
      }}
    >
      <FilterCheckbox
        disabled={disabled}
        checked={checked}
        onChange={onChange}
        label={displayName}
      />
      <StyledOnlyButton
        size="small"
        onClick={handleOnOnlyClick}
        appearance="secondary"
      >
        <FormattedMessage id="Filters.checkbox.selectOnly" />
      </StyledOnlyButton>
    </Container>
  )
}

export default OnlyableCheckbox
